export const API_URL = import.meta.env.FRONTEND_REDIRECT_URL || '';

import { createCorrelationId, trackApiOutcome } from '@/telemetry/clientTelemetry';

/**
 * Silently acquire a Bearer token from MSAL.
 * Returns null if no MSAL account is available — the backend will then fall
 * back to its cookie-based session auth for any remaining legacy sessions.
 * The old URLSafeSerializer localStorage blob is intentionally NOT sent as a
 * Bearer token: it is not a JWT and would cause a 401 on the backend.
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const { msalInstance, backendApiRequest } = await import('./msal');
    const redirectResult = await msalInstance.handleRedirectPromise().catch(() => null);
    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account);
    }

    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (account) {
      if (!msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(account);
      }
      const result = await msalInstance.acquireTokenSilent({
        ...backendApiRequest,
        account,
      });
      return result.accessToken;
    }
  } catch {
    // Silent acquisition failed (consent required, network error, etc.)
  }
  return null;
}

export async function fetcher<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const startedAt = performance.now();
  const correlationId = createCorrelationId();
  const method = options?.method || 'GET';

  try {
    // Always prefix API endpoints with /api
    const url = endpoint.startsWith('/api') ? `${API_URL}${endpoint}` : `${API_URL}${endpoint}`;

    // Acquire Bearer token (MSAL JWT preferred; legacy cookie blob as fallback)
    const token = await getAccessToken();
    const isProtectedApiCall = endpoint.startsWith('/api');
    if (isProtectedApiCall && !token) {
      throw new Error('Not authenticated: missing access token');
    }

    const responseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'x-correlation-id': correlationId,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };

    if (options?.headers) {
      const customHeaders = options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : Array.isArray(options.headers)
          ? Object.fromEntries(options.headers)
          : options.headers;
      Object.assign(responseHeaders, customHeaders);
    }

    const res = await fetch(url, {
      credentials: 'include',
      headers: responseHeaders,
      ...options,
    });
    
    // Maintenance mode: replace page content directly (avoids redirect loops in dev)
    if (res.status === 503) {
      const data = await res.json().catch(() => ({}));
      if (data.maintenance) {
        trackApiOutcome({
          endpoint,
          method,
          correlationId,
          latencyMs: performance.now() - startedAt,
          statusCode: 503,
          networkFailure: false,
          maintenanceMode: true,
        });
        document.documentElement.innerHTML = `
          <head><title>Bolt – Under Maintenance</title></head>
          <body style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;
            background:linear-gradient(135deg,#47A7DF 0%,#32769D 100%);min-height:100vh;
            display:flex;align-items:center;justify-content:center;">
            <div style="text-align:center;background:white;padding:3rem 4rem;border-radius:16px;
              box-shadow:0 20px 60px rgba(0,0,0,0.3);max-width:440px;">
              <div style="font-size:3rem;margin-bottom:1.5rem;">🔧</div>
              <h1 style="font-size:1.5rem;color:#333;margin-bottom:1rem;font-weight:600;">Bolt is under maintenance</h1>
              <p style="color:#666;line-height:1.6;margin:0;">We are working on some improvements.<br>
                Apologies for the inconvenience.<br>Please come back later.</p>
            </div>
          </body>`;
        return undefined as never;
      }
    }

    if (!res.ok) {
      trackApiOutcome({
        endpoint,
        method,
        correlationId,
        latencyMs: performance.now() - startedAt,
        statusCode: res.status,
        networkFailure: false,
        maintenanceMode: false,
      });
      const error = await res.text();
      const httpError = new Error(error || res.statusText) as Error & { status: number };
      httpError.status = res.status;
      throw httpError;
    }

    trackApiOutcome({
      endpoint,
      method,
      correlationId,
      latencyMs: performance.now() - startedAt,
      statusCode: res.status,
      networkFailure: false,
      maintenanceMode: false,
    });

    const data = await res.json() as Promise<T>;
    return data;
  } catch (error) {
    trackApiOutcome({
      endpoint,
      method,
      correlationId,
      latencyMs: performance.now() - startedAt,
      statusCode: undefined,
      networkFailure: true,
      maintenanceMode: false,
    });
    // propagate
    throw error;
  }
}